import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTasks,
  useTaskSections,
  useUpdateTask,
  useCreateTask,
  type Task,
} from '../../hooks/queries/useTasks';
import { useTasksStore } from '../../store/tasksStore';
import { useEffectiveTaskView } from '../../hooks/useEffectiveTaskView';
import { TaskSortToggle } from '../tasks/TaskSortToggle';
import { mergeDueDate } from '../../lib/tasks/taskReschedule';
import {
  DEFAULT_TASK_COMPLETION_FILTER,
  TASK_COMPLETION_FILTERS,
  completionToTaskFilters,
  type TaskCompletionFilter,
} from '../../lib/tasks/taskCompletionFilter';
import { TasksListView } from '../tasks/TasksListView';
import { TasksBoardView } from '../tasks/TasksBoardView';
import { TasksCalendarView } from '../tasks/TasksCalendarView';
import { TaskEditModal } from '../tasks/TaskEditModal';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { TaskTemplatePicker } from '../tasks/TaskTemplatePicker';
import { applyTaskTemplate } from '../../lib/tasks/applyTaskTemplate';
import { useSetTaskTags } from '../../hooks/queries/useTaskTags';
import type { TaskTemplate } from '../../services/tasks/taskTemplateService';
import { TasksBulkBar } from '../tasks/TasksBulkBar';
import { cn } from '../../lib/cn';
import { useAuthStore } from '../../store/useAuthStore';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { ListToolbar, Segments, ToolbarGrow, ViewSwitch } from '../ui/ListToolbar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FaCalendarDays, FaList, FaPlus, FaSquareKanban, FaTableList } from '../ui/FaIcons';
import { DossierTasksUrgencyList } from './DossierTasksUrgencyList';
import { TaskContextMenu, useTaskContextMenu } from '../tasks/TaskContextMenu';

interface Props {
  dossierId: string;
}

type DossierTaskView = 'grouped' | 'table' | 'board' | 'calendar';

/** « Liste » (groupée par urgence) et « Tableau » sont deux rendus de la vue liste du cabinet. */
const VIEWS: { value: DossierTaskView; mode: 'list' | 'board' | 'calendar'; label: string; icon: React.ReactNode }[] = [
  { value: 'grouped', mode: 'list', label: 'Liste', icon: <FaList aria-hidden /> },
  { value: 'table', mode: 'list', label: 'Tableau', icon: <FaTableList aria-hidden /> },
  { value: 'board', mode: 'board', label: 'Kanban', icon: <FaSquareKanban aria-hidden /> },
  { value: 'calendar', mode: 'calendar', label: 'Calendrier', icon: <FaCalendarDays aria-hidden /> },
];

const LIST_STYLE_KEY = 'cime.dossier.tasks.listStyle';

function readListStyle(): 'grouped' | 'table' {
  try {
    return localStorage.getItem(LIST_STYLE_KEY) === 'table' ? 'table' : 'grouped';
  } catch {
    return 'grouped';
  }
}

function FilterTitle({ children }: { children: string }) {
  return (
    <h5 className="mb-1 mt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-neutral-400 first:mt-0">
      {children}
    </h5>
  );
}

function FilterChoice({
  checked,
  onSelect,
  children,
}: {
  checked: boolean;
  onSelect: () => void;
  children: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-neutral-50">
      <input type="radio" checked={checked} onChange={onSelect} className="h-3.5 w-3.5 accent-cime-primary" />
      {children}
    </label>
  );
}

export function DossierTasksTab({ dossierId }: Props) {
  const setViewMode = useTasksStore((s) => s.setViewMode);
  const { viewMode, enabled, showSwitcher } = useEffectiveTaskView();
  const hoistSort = !showSwitcher && viewMode === 'list';
  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const selectedTaskIds = useTasksStore((s) => s.selectedTaskIds);
  const clearSelection = useTasksStore((s) => s.clearSelection);
  const [completionFilter, setCompletionFilter] = useState<TaskCompletionFilter>(
    DEFAULT_TASK_COMPLETION_FILTER,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [listStyle, setListStyle] = useState<'grouped' | 'table'>(readListStyle);
  const myId = useAuthStore((s) => s.collaborator?.id) ?? null;
  const canCreate = useCanAccessFeature('tasks_create');

  const { data: tasks = [], isLoading } = useTasks({
    ...(viewMode === 'list' ? {} : completionToTaskFilters(completionFilter)),
    dossier_id: dossierId,
  });
  const { data: sections = [] } = useTaskSections(dossierId);

  // Exclut les taches en suppression differee : sans ce filtre, une tache
  // supprimee via sa corbeille resterait comptee dans la barre et recevrait
  // les actions groupees pendant sa fenetre d'annulation.
  const selectedTasks = useMemo(
    () => tasks.filter((t) => selectedTaskIds.has(t.id) && !pendingDeleteIds.has(t.id)),
    [tasks, selectedTaskIds, pendingDeleteIds],
  );

  // La sélection multiple ne doit pas survivre à un changement de contexte
  // (autre vue ou autre dossier via la fiche) : les ids sélectionnés
  // perdraient leur sens visuel.
  useEffect(() => {
    clearSelection();
  }, [viewMode, dossierId, clearSelection]);

  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const taskMenu = useTaskContextMenu();

  const editingTask = useMemo(
    () => (editingTaskId ? tasks.find((t) => t.id === editingTaskId) ?? null : null),
    [editingTaskId, tasks],
  );

  function handleOpenTask(task: Task) {
    setEditingTaskId(task.id);
  }

  const updateTask = useUpdateTask();
  function handleReschedule(task: Task, isoDay: string) {
    updateTask.mutate({ taskId: task.id, updates: mergeDueDate(task, isoDay) });
  }

  const createTaskMutation = useCreateTask();
  function handleQuickCreate(isoDay: string, title: string) {
    createTaskMutation.mutate(
      {
        content: title,
        priority: 3,
        due_date: isoDay,
        dossier_id: dossierId,
        section_id: null,
      },
      {
        onError: (err) => {
          toast.error(`Impossible de créer la tâche : ${err.message}`);
        },
      },
    );
  }

  const grouped = viewMode === 'list' && listStyle === 'grouped';
  const currentView: DossierTaskView = viewMode === 'list' ? listStyle : viewMode;
  const viewOptions = VIEWS.filter((v) => v.mode === 'list' || enabled.includes(v.mode));

  function selectView(next: DossierTaskView) {
    const option = VIEWS.find((v) => v.value === next);
    if (!option) return;
    if (option.mode === 'list') {
      const style = next === 'table' ? 'table' : 'grouped';
      setListStyle(style);
      try {
        localStorage.setItem(LIST_STYLE_KEY, style);
      } catch {
        // stockage indisponible : le choix vaut pour la page seulement
      }
    }
    if (option.mode !== viewMode) setViewMode(option.mode);
  }

  function submitQuickTask() {
    const title = quickTitle.trim();
    if (!title) return;
    createTaskMutation.mutate(
      { content: title, priority: 3, dossier_id: dossierId, section_id: null },
      {
        onSuccess: () => setQuickTitle(''),
        onError: (err) => toast.error(`Impossible de créer la tâche : ${err.message}`),
      },
    );
  }

  const setTaskTags = useSetTaskTags();
  /** Crée la tâche depuis une tâche type. Un titre déjà tapé dans la barre remplace celui du modèle. */
  function createFromTemplate(template: TaskTemplate) {
    const applied = applyTaskTemplate(
      {
        content: template.content,
        description: template.description,
        priority: template.priority,
        tagIds: template.tag_ids,
        estimated_duration_minutes: template.estimated_duration_minutes,
      },
      { title: quickTitle, parsedPriority: null, parsedTagIds: [], dueDate: null },
    );
    createTaskMutation.mutate(
      {
        content: applied.content,
        description: applied.description,
        priority: applied.priority,
        estimated_duration_minutes: applied.estimated_duration_minutes,
        dossier_id: dossierId,
        section_id: null,
        task_template_id: template.id,
      },
      {
        onSuccess: (task) => {
          setQuickTitle('');
          if (applied.tagIds.length > 0) setTaskTags.mutate({ taskId: task.id, tagIds: applied.tagIds });
        },
        onError: (err) => toast.error(`Impossible de créer la tâche : ${err.message}`),
      },
    );
  }

  const filterLabel = `${showDone ? 'Toutes' : 'Ouvertes'} · ${onlyMine ? 'moi' : 'tout le monde'}`;

  const toolbar = (
      <ListToolbar className={grouped ? undefined : 'mb-3 flex-shrink-0'}>
        {canCreate ? (
          <label className="flex h-10 min-w-[260px] flex-1 items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-3.5 focus-within:border-cime-primary">
            <FaPlus className="h-3 w-3 flex-shrink-0 text-neutral-400" aria-hidden />
            <input
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitQuickTask();
              }}
              disabled={createTaskMutation.isPending}
              placeholder="Ajouter une tâche… puis Entrée"
              aria-label="Ajouter une tâche"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-cime-text-primary outline-none placeholder:text-neutral-400"
            />
            <TaskTemplatePicker compact onSelect={createFromTemplate} />
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              title="Ouvrir la fiche de création complète"
              className="flex-shrink-0 rounded-md px-2 py-1 text-xs text-cime-text-muted hover:bg-neutral-100 hover:text-cime-primary"
            >
              Détailler
            </button>
          </label>
        ) : (
          <ToolbarGrow />
        )}
        {grouped ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3.5 text-[13px] text-cime-text-primary hover:border-neutral-300 data-[state=open]:border-cime-primary"
              >
                {filterLabel}
                <svg viewBox="0 0 12 12" aria-hidden className="h-3 w-3 text-neutral-400">
                  <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 rounded-xl border-neutral-200 bg-white p-3 text-[13px]">
              <FilterTitle>Statut</FilterTitle>
              <FilterChoice checked={!showDone} onSelect={() => setShowDone(false)}>Ouvertes</FilterChoice>
              <FilterChoice checked={showDone} onSelect={() => setShowDone(true)}>Toutes</FilterChoice>
              <FilterTitle>Assignées à</FilterTitle>
              <FilterChoice checked={!onlyMine} onSelect={() => setOnlyMine(false)}>Tout le monde</FilterChoice>
              <FilterChoice checked={onlyMine} onSelect={() => setOnlyMine(true)}>Moi uniquement</FilterChoice>
            </PopoverContent>
          </Popover>
        ) : null}
        {viewMode !== 'list' ? (
          <Segments
            label="Statut des tâches"
            value={completionFilter}
            onChange={setCompletionFilter}
            options={TASK_COMPLETION_FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          />
        ) : null}
        {hoistSort && viewMode !== 'list' && <TaskSortToggle />}
        <ViewSwitch label="Affichage" options={viewOptions} value={currentView} onChange={selectView} />
      </ListToolbar>
  );

  return (
    <div className={cn('flex flex-col', !grouped && 'h-full overflow-hidden rounded-xl border border-neutral-200 bg-white px-5 py-4')}>
      {grouped ? null : toolbar}

      <div
        className={cn(!grouped && 'flex-1 min-h-0 overflow-auto')}
        onContextMenu={(e) => taskMenu.openFrom(e, tasks)}
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-cime-text-muted">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Chargement des tâches…
          </div>
        ) : (
          <>
            {grouped && (
              <DossierTasksUrgencyList
                tasks={tasks.filter((t) => !pendingDeleteIds.has(t.id) && !t.is_deleted)}
                showDone={showDone}
                onlyAssigneeId={onlyMine ? myId : null}
                onOpenTask={handleOpenTask}
                toolbar={toolbar}
                previewPaused={editingTaskId !== null || createOpen}
              />
            )}
            {viewMode === 'list' && !grouped && (
              <TasksListView
                tasks={tasks}
                sections={sections}
                dossierId={dossierId}
                onOpenTask={handleOpenTask}
                lockedGroupMode="sections"
                hideComposer
                hideSort={hoistSort}
              />
            )}
            {viewMode === 'board' && (
              <TasksBoardView
                tasks={tasks}
                sections={sections}
                dossierId={dossierId}
                onOpenTask={handleOpenTask}
                hideComposer
              />
            )}
            {viewMode === 'calendar' && (
              <div className="overflow-x-auto">
                <TasksCalendarView
                  tasks={tasks.filter((t) => !pendingDeleteIds.has(t.id))}
                  onOpenTask={handleOpenTask}
                  onReschedule={handleReschedule}
                  onQuickCreate={handleQuickCreate}
                />
              </div>
            )}
          </>
        )}
      </div>

      {taskMenu.target ? (
        <TaskContextMenu target={taskMenu.target} onClose={taskMenu.close} onOpenTask={handleOpenTask} />
      ) : null}

      <TaskEditModal
        task={editingTask}
        sections={sections}
        onClose={() => setEditingTaskId(null)}
      />

      <TaskCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        dossierId={dossierId}
      />

      {viewMode !== 'calendar' && selectedTasks.length > 0 && (
        <TasksBulkBar
          selectedTasks={selectedTasks}
          activeDossierId={dossierId}
          onClear={clearSelection}
        />
      )}
    </div>
  );
}
